/**
 * Push notifications (client-side scaffold).
 *
 * This wires up everything the *device* needs: permission request, APNs
 * registration, token capture, and tap handling. What it deliberately does
 * NOT do yet is deliver the device token to a backend or send pushes — that
 * requires an Apple Developer Program APNs auth key and a server (a Supabase
 * Edge Function is the intended home). See the TODO in `registerDeviceToken`.
 *
 * No-op on web.
 */

import { PushNotifications, type Token } from '@capacitor/push-notifications';
import type { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { getPlatform, isNativePlatform } from './platform';
import { supabase } from '@/lib/supabase';

let initialized = false;

/** Handler invoked when the user taps a push (used for deep-linking). */
type TapHandler = (data: Record<string, unknown>) => void;
let tapHandler: TapHandler | null = null;

/** Register a handler for notification taps. Set before `initPushNotifications`. */
export function onPushTap(handler: TapHandler): void {
  tapHandler = handler;
}

/**
 * Deliver the APNs device token to the backend so it can target this device.
 *
 * Calls the `register-device` Edge Function, which upserts
 * { user_id, token, platform, environment } into the `device_tokens` table.
 * The `send-push` function later reads that table and calls APNs with the
 * team's APNs auth key (.p8) — see supabase/functions/send-push.
 *
 * `environment` tells the sender which APNs host to use: debug builds get a
 * sandbox token, TestFlight / App Store builds get a production token. We
 * approximate this from Vite's mode (import.meta.env.DEV).
 */
async function registerDeviceToken(token: string): Promise<void> {
  if (!supabase) return;
  const environment = import.meta.env.DEV ? 'sandbox' : 'production';
  const { error } = await supabase.functions.invoke('register-device', {
    body: { token, platform: getPlatform(), environment },
  });
  if (error) {
    // eslint-disable-next-line no-console
    console.error('[push] failed to register device token:', error.message);
  }
}

/**
 * Request permission and register for push. Safe to call once at startup after
 * the user is authenticated. Returns true if registration was initiated.
 */
export async function initPushNotifications(): Promise<boolean> {
  if (!isNativePlatform() || initialized) return false;
  // Android push requires Firebase Cloud Messaging (a google-services.json in
  // the Android project). That isn't configured yet, and calling
  // PushNotifications.register() without it throws a native
  // IllegalStateException ("Default FirebaseApp is not initialized") on the
  // CapacitorPlugins thread, which crashes the whole app on launch. Until FCM
  // is set up, skip push on Android; iOS (APNs) is unaffected. Push delivery
  // to the backend is still a TODO regardless (see registerDeviceToken).
  if (getPlatform() === 'android') return false;
  initialized = true;

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return false;

  // Registration result comes back asynchronously via the 'registration' event.
  await PushNotifications.addListener('registration', (token: Token) => {
    void registerDeviceToken(token.value);
  });

  await PushNotifications.addListener('registrationError', (err) => {
    // eslint-disable-next-line no-console
    console.error('[push] registration error:', err);
  });

  await PushNotifications.addListener(
    'pushNotificationReceived',
    (_notification: PushNotificationSchema) => {
      // Foreground receipt. presentationOptions in capacitor.config.ts already
      // shows the system banner; hook additional in-app UI here if desired.
    },
  );

  await PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      const data = (action.notification.data ?? {}) as Record<string, unknown>;
      tapHandler?.(data);
    },
  );

  await PushNotifications.register();
  return true;
}

/** Clear the app icon badge (e.g. when the user opens the app). */
export async function clearPushBadge(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch {
    /* ignore */
  }
}
